import React, { useState, useRef, useEffect } from 'react';
import { Upload, Printer, Save, Loader2, Calendar, DollarSign, Plus, Minus, FileText, File, Search, Sparkles, CheckCircle } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import PrintableDocument from '@/components/PrintableDocument';
import BillsService from '@/api/bills.service';
import PaymentMethodsService from '@/api/paymentmethods.service';
import ProductServicesService from '@/api/productservices.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PayToSection from '@/components/PayToSection';
import { DocumentUpload } from '@/components/DocumentUpload';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';

// Supplier type to match the schema
type Supplier = {
  id: number;
  publicId?: string;
  businessId?: number;
  name: string;
  contactPerson: string;
  email?: string;
  phoneNumber: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  taxId?: string;
  notes?: string;
  status?: string;
  timezone?: string;
  dateUpdated?: string;
  dateCreated?: string;
};

export default function DocumentForm() {
  const [logo, setLogo] = useState<string>();
  const [signature, setSignature] = useState<string>();
  const [currency, setCurrency] = useState<Currency>('USD');
  const [items, setItems] = useState([{ name: '', description: '', quantity: 1, unitPrice: 0 }]);
  const [status] = useState('pending');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    emailAddress: '',
    phoneNumber: '',
    logoImg: ''
  });
  const [supplierDetails, setSupplierDetails] = useState<Supplier>({
    id: 0,
    publicId: '',
    name: '',
    contactPerson: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: ''
  });
  const [supplierData, setSupplierData] = useState<Supplier[]>([]);
  const [taxes, setTaxes] = useState([{ taxName: '', taxRate: 0, taxAmount: 0 }]);
  const [billNumber, setBillNumber] = useState('');
  const [billPublicId, setBillPublicId] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isPaymentMethodsLoaded, setIsPaymentMethodsLoaded] = useState(false);
  
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [billDocument, setBillDocument] = useState<File | null>(null);
  const [receiptDocument, setReceiptDocument] = useState<File | null>(null);
  const [receiptDocumentProgress, setReceiptDocumentProgress] = useState(0);
  const [customReceiptNumber, setCustomReceiptNumber] = useState('');

  const printableRef = useRef<PrintableRef>(null);

  const { toast } = useToast();
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const [searchProduct, setSearchProduct] = useState('');
  const [isSearchingProduct, setIsSearchingProduct] = useState(false);
  const [productData, setProductData] = useState<any[]>([]);

  // AI Document Processing states
  const [showAiTab, setShowAiTab] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [isApplyingExtraction, setIsApplyingExtraction] = useState(false);

  useEffect(() => {
    if (selectedBusiness) {
      setCompanyDetails({
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        country: selectedBusiness.country,
        emailAddress: selectedBusiness.emailAddress,
        phoneNumber: selectedBusiness.phoneNumber,
        logoImg: selectedBusiness.logoImg
      });
      setLogo(selectedBusiness.logoImg);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await PaymentMethodsService.getAllPaymentMethods();
        if (response.data.result === 'success') {
          setPaymentMethods(response.data.paymentMethods);
        } else {
          showToastWithTimeout("error", "Error", "Failed to fetch payment methods", 3000);
        }
      } catch (error) {
        showToastWithTimeout("error", "Error", "Failed to fetch payment methods", 3000);
      } finally {
        setIsPaymentMethodsLoaded(true);
      }
    };

    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchProduct.length > 0) {
        handleSearchProduct();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchProduct]);

  const handleSearchProduct = async () => {
    setIsSearchingProduct(true);
    try {
      const response = await ProductServicesService.searchProductService(selectedBusiness.id, searchProduct);
      const { data } = response;
      if (data.result === 'success') {
        setProductData(data.productServices);
        showToastWithTimeout("success", "✅ Product Search Successful", "Product search successful", 3000);
      } else {
        showToastWithTimeout("error", "❌ Product Search Failed", data.message, 3000);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSearchingProduct(false);
    }
  };

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignature(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const handleBillDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBillDocument(file);
    }
  };

  const handleReceiptDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptDocument(file);
    }
  };

  const handleMarkAsPaid = () => {
    updateBillStatus('paid', calculateTotal());
    setModalOpen(false);
  };

  const handleMarkAsPartiallyPaid = () => {
    updateBillStatus('partially_paid', amountPaid);
    setModalOpen(false);
  };

  const updateBillStatus = async (status: string, amount: any) => {
    try {
      setIsMarking(true);
      
      // Create form data
      const formData = new FormData();
      formData.append('status', status);
      formData.append('amount', amount);
      formData.append('paymentMethodId', paymentMethodId);
      
      // Format date to DD/MM/YYYY as expected by the API
      const formatDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      };
      
      formData.append('paymentDate', formatDate(paymentDate));
      
      if (paymentReference) {
        formData.append('paymentReference', paymentReference);
      }
      
      if (customReceiptNumber) {
        formData.append('receiptNumber', customReceiptNumber);
      }
      
      if (receiptDocument) {
        formData.append('receiptDocument', receiptDocument);
      }

      const response = await BillsService.updateSpecificBill(
        billPublicId, 
        formData,
        progressEvent => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);
          setReceiptDocumentProgress(percentCompleted);
        }
      );
      
      if (response.data.result === 'success') {
        showToastWithTimeout("default", `✅ Marked as ${status.replace('_', ' ').toUpperCase()}`, 
          `Bill #${billNumber} has been marked as ${status.replace('_', ' ')}${amount ? ` with amount ${amount}` : ''} and Receipt #${response.data.receiptNumber} has been created.`, 
          5000);
        setTimeout(() => {
          if (status === 'paid') window.location.href = `/receipt/${response.data.receiptId}`;
        }, 2000);
      } else {
        showToastWithTimeout("error", "Error", response.data.message, 3000);
      }
    } catch (error) {
      console.log("updateSpecificBill error", error);
      showToastWithTimeout("error", "Error", "Failed to update bill status", 3000);
    } finally {
      setIsMarking(false);
    }
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      showToastWithTimeout("error", "❌ Error", "Please add at least one item", 3000);
      return;
    }

    setIsSaving(true);
    try {
      // Create form data for file upload
      const formData = new FormData();
      
      // Add form fields
      formData.append('billNumber', billNumber);
      formData.append('supplierPublicId', supplierDetails.publicId ?? '');
      formData.append('userId', selectedBusiness.id.toString());
      formData.append('businessId', selectedBusiness.id.toString());
      formData.append('amount', calculateTotal().toString());
      formData.append('currency', currency);
      formData.append('status', status);
      
      // Format dates to DD/MM/YYYY as expected by the API
      const formatDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      };
      
      formData.append('issueDate', formatDate(issueDate));
      formData.append('dueDate', formatDate(dueDate));
      formData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
      
      // Add bill items
      formData.append('billItems', JSON.stringify(items.map(item => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.quantity * item.unitPrice,
      }))));
      
      // Add bill taxes
      formData.append('billTaxes', JSON.stringify(taxes.map(tax => ({
        name: tax.taxName,
        rate: tax.taxRate,
        taxAmount: calculateSubtotal() * (tax.taxRate / 100),
      }))));
      
      // Add notes, terms, signature
      formData.append('notes', notes);
      formData.append('terms', terms);
      if (signature) {
        formData.append('signature', signature);
      }
      
      // Add bill document if it exists
      if (billDocument) {
        formData.append('billDocument', billDocument);
      }

      const response = await BillsService.createBill(
        formData, 
        parseInt(selectedBusiness.id),
        progressEvent => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);
          setUploadProgress(percentCompleted);
        }
      );
      
      if (response.data.result === 'success') {
        showToastWithTimeout("default", "✅ Bill Created", "Bill has been created successfully", 3000);
        setBillPublicId(response.data.publicId);
      } else {
        showToastWithTimeout("error", "❌ Error", response.data.message, 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Error", "An error occurred while saving the bill", 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const calculateTotalTax = () => {
    return taxes.reduce((sum, tax) => sum + tax.taxAmount, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTotalTax();
  };

  const addItem = () => {
    setItems([...items, { name: '', description: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const addTax = () => {
    setTaxes([...taxes, { taxName: '', taxRate: 0, taxAmount: 0 }]);
  };

  const removeTax = (index: number) => {
    if (taxes.length > 1) {
      setTaxes(taxes.filter((_, i) => i !== index));
    }
  };

  // Handle AI extraction completion
  const handleExtractionComplete = (data: any) => {
    setExtractedData(data);
    setShowAiTab(true);
  };

  // Apply extracted data to form
  const applyExtractedData = () => {
    if (!extractedData) return;
    
    setIsApplyingExtraction(true);
    
    try {
      // Apply vendor/supplier information
      if (extractedData.vendorName) {
        // Try to find matching supplier
        const matchingSupplier = supplierData.find(supplier => 
          supplier.name?.toLowerCase().includes(extractedData.vendorName.toLowerCase())
        );
        
        if (matchingSupplier) {
          setSupplierDetails(matchingSupplier);
        } else {
          // Create new supplier data from extraction
          setSupplierDetails(prev => ({
            ...prev,
            name: extractedData.vendorName,
            address: extractedData.vendorAddress || prev.address
          }));
        }
      }

      // Apply bill number
      if (extractedData.invoiceNumber) {
        setBillNumber(extractedData.invoiceNumber);
      }

      // Apply dates
      if (extractedData.invoiceDate) {
        const date = new Date(extractedData.invoiceDate);
        if (!isNaN(date.getTime())) {
          setIssueDate(date.toISOString().split('T')[0]);
        }
      }

      // Apply currency
      if (extractedData.currency) {
        setCurrency(extractedData.currency as Currency);
      }

      // Apply line items
      if (extractedData.lineItems && extractedData.lineItems.length > 0) {
        const newItems = extractedData.lineItems.map((item: any) => ({
          name: item.description || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0
        }));
        setItems(newItems);
      } else if (extractedData.totalAmount) {
        // If no line items but total amount, create a single item
        setItems([{
          name: 'Extracted Item',
          description: 'Item extracted from document',
          quantity: 1,
          unitPrice: extractedData.totalAmount
        }]);
      }

      // Apply tax information
      if (extractedData.taxAmount && extractedData.totalAmount) {
        const taxRate = (extractedData.taxAmount / (extractedData.totalAmount - extractedData.taxAmount)) * 100;
        setTaxes([{
          taxName: 'Extracted Tax',
          taxRate: taxRate,
          taxAmount: extractedData.taxAmount
        }]);
      }

      toast({
        title: "Data Applied",
        description: "Extracted data has been applied to the form",
      });
    } catch (error) {
      console.error('Error applying extracted data:', error);
      toast({
        title: "Error",
        description: "Failed to apply extracted data",
        variant: "destructive",
      });
    } finally {
      setIsApplyingExtraction(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Create Bill</h1>
          <p className="text-muted-foreground">Create a new bill for your supplier</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={currency} onValueChange={(value) => setCurrency(value as Currency)}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD ($)</SelectItem>
              <SelectItem value="ZWG">ZWG ($)</SelectItem>
              <SelectItem value="ZAR">ZAR (R)</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={handlePrint} 
            disabled={!billNumber}
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            <span>Print</span>
          </Button>
          
          <Button 
            onClick={handleSubmit} 
            disabled={isSaving}
            className="flex items-center gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>Save Bill</span>
          </Button>
          
          {billPublicId && (
            <Button 
              variant="secondary" 
              onClick={() => setModalOpen(true)} 
              disabled={isMarking}
              className="flex items-center gap-2"
            >
              {isMarking ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <DollarSign className="h-4 w-4" />
              )}
              <span>Mark as Paid</span>
            </Button>
          )}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Mark Bill as Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amountPaid">Amount Paid ({currency})</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="amountPaid"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="customReceiptNumber">Receipt Number (Optional)</Label>
              <Input
                id="customReceiptNumber"
                placeholder="Custom receipt number"
                value={customReceiptNumber}
                onChange={(e) => setCustomReceiptNumber(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="paymentMethod">Payment Method</Label>
              {isPaymentMethodsLoaded ? (
                <Select value={paymentMethodId} onValueChange={setPaymentMethodId}>
                  <SelectTrigger id="paymentMethod">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods && paymentMethods.map((method: any) => (
                      <SelectItem key={method.id} value={method.id.toString()}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex justify-center items-center p-2 border rounded-md">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading payment methods...</span>
                </div>
              )}
            </div>
            
            {paymentMethodId === '4' && (
              <div className="space-y-2">
                <Label htmlFor="paymentReference">Payment Reference</Label>
                <Input
                  id="paymentReference"
                  placeholder="Enter payment reference"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="paymentDate">Payment Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="paymentDate"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="receiptDocument">Receipt Document (Optional)</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf,.png,.jpg,.jpeg';
                    input.onchange = handleReceiptDocumentUpload as any;
                    input.click();
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {receiptDocument ? receiptDocument.name : 'Upload Receipt'}
                </Button>
                {receiptDocument && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setReceiptDocument(null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Minus size={16} />
                  </Button>
                )}
              </div>
              {receiptDocumentProgress > 0 && receiptDocumentProgress < 100 && (
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${receiptDocumentProgress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setModalOpen(false)} 
              className="sm:order-1"
            >
              Cancel
            </Button>
            <Button 
              variant="default" 
              onClick={handleMarkAsPaid} 
              disabled={amountPaid !== '' || !paymentMethodId}
              className="sm:order-3"
            >
              Mark as Fully Paid
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleMarkAsPartiallyPaid} 
              disabled={amountPaid === '' || !paymentMethodId}
              className="sm:order-2"
            >
              Mark as Partially Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Form with Tabs */}
      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="ai">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Processing
          </TabsTrigger>
          {showAiTab && (
            <TabsTrigger value="extracted">
              Extracted Data
              {extractedData && <span className="ml-2 w-2 h-2 bg-green-500 rounded-full"></span>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
          <PayToSection
            supplierDetails={supplierDetails}
            setSupplierDetails={setSupplierDetails}
            supplierData={supplierData}
            setSupplierData={setSupplierData}
          />

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
              <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Bill Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="billNumber" className="text-sm font-medium text-slate-700">Bill Number (Invoice Number)</Label>
                  <Input
                    id="billNumber"
                    placeholder="Enter bill number from supplier"
                    value={billNumber}
                    onChange={(e) => setBillNumber(e.target.value)}
                    className="border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="billDocument" className="text-sm font-medium text-slate-700">Bill Document (Optional)</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.pdf,.png,.jpg,.jpeg';
                        input.onchange = handleBillDocumentUpload as any;
                        input.click();
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {billDocument ? billDocument.name : 'Upload Bill Document'}
                    </Button>
                    {billDocument && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setBillDocument(null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Minus size={16} />
                      </Button>
                    )}
                  </div>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Important Dates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="issueDate" className="text-sm font-medium text-slate-700">Issue Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-blue-500" />
                    <Input
                      id="issueDate"
                      type="date"
                      value={issueDate}
                      onChange={(e) => setIssueDate(e.target.value)}
                      className="pl-10 border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dueDate" className="text-sm font-medium text-slate-700">Due Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-blue-500" />
                    <Input
                      id="dueDate"
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="pl-10 border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                Line Items
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4 mt-4">
                <div className='flex items-center my-4'>
                  <Input
                    type="text"
                    placeholder="Search for product/service"
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                    className="mr-2"
                  />
                  <Button onClick={handleSearchProduct} disabled={isSearchingProduct}>
                    {isSearchingProduct ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {productData.length > 0 && (
                  <select
                    onChange={(e) => {
                      const selectedProduct = productData.find((product) => product.id === Number(e.target.value));
                      if (selectedProduct) {
                        // Check if the last item is empty (default values)
                        const lastItem = items[items.length - 1];
                        const isLastItemEmpty = 
                          lastItem.name === '' && 
                          lastItem.description === '' && 
                          lastItem.quantity === 1 && 
                          lastItem.unitPrice === 0;
                        
                        const newItems = [...items];
                        
                        if (isLastItemEmpty) {
                          // Replace the empty item
                          newItems[items.length - 1] = {
                            name: selectedProduct.name,
                            description: selectedProduct.description,
                            quantity: 1,
                            unitPrice: selectedProduct.unitPrice
                          };
                        } else {
                          // Append a new item
                          newItems.push({
                            name: selectedProduct.name,
                            description: selectedProduct.description,
                            quantity: 1,
                            unitPrice: selectedProduct.unitPrice
                          });
                        }
                        setItems(newItems);
                        setProductData([]);
                        setSearchProduct('');
                      }
                    }}
                    className="w-full p-2 border rounded-lg bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                  >
                    <option value="" disabled selected>Select a product/service</option>
                    {productData.map((product) => (
                      <option
                        key={product.id}
                        value={String(product.id)}
                        className="p-2 border-b border-gray-200 hover:bg-gray-100"
                      >
                        {product.name} (${product.unitPrice})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {items.map((item, index) => (
                <Card key={index} className="border border-slate-200 overflow-hidden">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-4 space-y-2">
                        <Label htmlFor={`item-name-${index}`} className="text-sm font-medium text-slate-700">Item Name</Label>
                        <Input
                          id={`item-name-${index}`}
                          type="text"
                          placeholder="Product or service name"
                          className="border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={item.name}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[index].name = e.target.value;
                            setItems(newItems);
                          }}
                        />
                      </div>
                      
                      <div className="md:col-span-4 space-y-2">
                        <Label htmlFor={`item-desc-${index}`} className="text-sm font-medium text-slate-700">Description</Label>
                        <Textarea
                          id={`item-desc-${index}`}
                          placeholder="Item description"
                          className="min-h-[80px] resize-none border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={item.description}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[index].description = e.target.value.slice(0, 255);
                            setItems(newItems);
                          }}
                          maxLength={255}
                        />
                      </div>
                      
                      <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`item-qty-${index}`} className="text-sm font-medium text-slate-700">Quantity</Label>
                          <Input
                            id={`item-qty-${index}`}
                            type="number"
                            min="1"
                            placeholder="Qty"
                            className="border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={item.quantity}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index].quantity = Number(e.target.value);
                              setItems(newItems);
                            }}
                          />
                        </div>
                        
                        <div className="space-y-2 col-span-2 md:col-span-1">
                          <Label htmlFor={`item-price-${index}`} className="text-sm font-medium text-slate-700">Unit Price</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                            <Input
                              id={`item-price-${index}`}
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="pl-10 border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              value={item.unitPrice}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[index].unitPrice = Number(e.target.value);
                                setItems(newItems);
                              }}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2 md:col-span-1 flex items-end">
                          <Button
                            type="button"
                            onClick={() => {
                              const newItems = items.filter((_, i) => i !== index);
                              setItems(newItems.length ? newItems : [{ name: '', description: '', quantity: 1, unitPrice: 0 }]);
                            }}
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-gray-500 hover:text-red-500"
                          >
                            <Minus size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <Button
                type="button"
                onClick={() => {
                  setItems([...items, { name: '', description: '', quantity: 1, unitPrice: 0 }]);
                }}
                variant="outline"
                className="w-full border-dashed flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
              >
                <Plus size={16} />
                Add Item
              </Button>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-500" />
                Taxes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <AnimatePresence>
                  {taxes.map((tax, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end border border-slate-200 rounded-lg p-4 bg-white"
                    >
                      <div className="md:col-span-5 space-y-2">
                        <Label htmlFor={`tax-name-${index}`} className="text-sm font-medium text-slate-700">Tax Name</Label>
                        <Input
                          id={`tax-name-${index}`}
                          type="text"
                          placeholder="e.g. VAT, Sales Tax"
                          className="border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={tax.taxName}
                          onChange={(e) => {
                            const newTaxes = [...taxes];
                            newTaxes[index].taxName = e.target.value;
                            setTaxes(newTaxes);
                          }}
                        />
                      </div>
                      
                      <div className="md:col-span-3 space-y-2">
                        <Label htmlFor={`tax-rate-${index}`} className="text-sm font-medium text-slate-700">Rate (%)</Label>
                        <Input
                          id={`tax-rate-${index}`}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={tax.taxRate}
                          onChange={(e) => {
                            const newTaxes = [...taxes];
                            newTaxes[index].taxRate = Number(e.target.value.replace(/^0+/, ''));
                            newTaxes[index].taxAmount = calculateSubtotal() * (newTaxes[index].taxRate / 100);
                            setTaxes(newTaxes);
                          }}
                        />
                      </div>
                      
                      <div className="md:col-span-3 space-y-2">
                        <Label htmlFor={`tax-amount-${index}`} className="text-sm font-medium text-slate-700">Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                          <Input
                            id={`tax-amount-${index}`}
                            type="number"
                            placeholder="0.00"
                            className="pl-10 border-slate-200 rounded-lg bg-slate-50 text-slate-700"
                            value={tax.taxAmount.toFixed(2)}
                            readOnly
                          />
                        </div>
                      </div>
                      
                      <div className="md:col-span-1 flex justify-end">
                        <Button
                          type="button"
                          onClick={() => {
                            const newTaxes = taxes.filter((_, i) => i !== index);
                            setTaxes(newTaxes.length ? newTaxes : [{ taxName: '', taxRate: 0, taxAmount: 0 }]);
                          }}
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 text-gray-500 hover:text-red-500"
                        >
                          <Minus size={16} />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                <Button
                  type="button"
                  onClick={() => {
                    setTaxes([...taxes, { taxName: '', taxRate: 0, taxAmount: 0 }]);
                  }}
                  variant="outline"
                  className="w-full border-dashed flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                >
                  <Plus size={16} />
                  Add Tax
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
                <CardTitle className="text-lg font-medium text-gray-800">Notes & Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-sm font-medium text-slate-700">Notes to Supplier</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any additional notes for your records"
                    className="min-h-[120px] resize-none border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2 mt-4">
                  <Label htmlFor="terms" className="text-sm font-medium text-slate-700">Terms & Conditions</Label>
                  <Textarea
                    id="terms"
                    placeholder="Add your terms and conditions"
                    className="min-h-[120px] resize-none border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-6">
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
                  <CardTitle className="text-lg font-medium text-gray-800">Signature</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-white">
                    {signature ? (
                      <div className="relative">
                        <img src={signature} alt="Signature" className="max-h-32 mx-auto" />
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="absolute top-0 right-0 text-slate-500 hover:text-red-500"
                          onClick={() => setSignature(undefined)}
                        >
                          <Minus size={16} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Upload className="h-8 w-8 text-slate-400" />
                        <p className="text-sm text-slate-600">Upload your signature</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="mt-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = handleSignatureUpload as any;
                            input.click();
                          }}
                        >
                          Select File
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
                  <CardTitle className="text-lg font-medium text-gray-800">Bill Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 p-6 bg-gradient-to-br from-white to-gray-50 rounded-lg border border-gray-100">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal:</span>
                      <span className="font-medium">{currency} {calculateSubtotal().toFixed(2)}</span>
                    </div>
                    <Separator className="bg-gray-200" />
                    <div className="flex justify-between text-gray-600">
                      <span>Tax:</span>
                      <span className="font-medium">{currency} {calculateTotalTax().toFixed(2)}</span>
                    </div>
                    <Separator className="bg-gray-200" />
                    <div className="flex justify-between text-lg font-semibold text-gray-900">
                      <span>Total:</span>
                      <span className="text-blue-600">{currency} {calculateTotal().toFixed(2)}</span>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 px-3 py-1">
                        Status: Pending
                      </Badge>
                      {billNumber && (
                        <div className="mt-2 text-sm text-gray-500">
                          Bill Number: <span className="font-medium text-gray-700">{billNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6">
          {/* AI processing content */}
        </TabsContent>

        <TabsContent value="extracted" className="space-y-6">
          {/* Extracted data content */}
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-4 mt-8">
        <Button 
          variant="outline"
          className="px-6 border-slate-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
          onClick={() => printableRef.current?.print()}
          disabled={billPublicId === ''}
        >
          <Printer className="mr-2 h-4 w-4" />
          Preview
        </Button>
        <Button 
          className="px-6 shadow-md hover:shadow-lg transition-all"
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {uploadProgress > 0 ? `Uploading ${uploadProgress}%` : 'Saving...'}
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Bill
            </>
          )}
        </Button>
      </div>

      <div className="hidden">
        <PrintableDocument
          ref={printableRef}
          document={{
            id: 'temp-id',
            number: billNumber,
            type: 'bill',
            date: new Date().toLocaleDateString('en-GB'),
            currency,
            items,
            status: 'pending',
            companyDetails,
            supplierDetails: {
              name: supplierDetails.name,
              address: supplierDetails.address || '',
              emailAddress: supplierDetails.email || '',
              phoneNumber: supplierDetails.phoneNumber,
              companyName: supplierDetails.name
            },
            notes,
            terms,
            logo,
            signature,
            taxes: taxes.map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: tax.taxAmount,
            })),
            issueDate: issueDate.split('-').reverse().join('/'),
            dueDate: dueDate.split('-').reverse().join('/'),
          }}
        />
      </div>
    </div>
  );
}